import { useEffect, useRef } from "react";
import { HubConnection } from "@microsoft/signalr";

interface UseSignalROptions {
  once?: boolean;
}

export function useSignalR<T extends (...args: unknown[]) => void>(
  connection: HubConnection | null,
  eventName: string,
  handler: T,
  options: UseSignalROptions = {}
) {
  const { once = false } = options;

  // Keep a ref to the latest handler so the stable proxy always calls the
  // current version without needing to re-register with SignalR.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!connection) return;
   
    const stableHandler = ((...args: unknown[]) => {
      handlerRef.current(...(args as Parameters<T>));
      if (once) {
        connection.off(eventName, stableHandler);
      }
    }) as T;

    connection.on(eventName, stableHandler);

    return () => {
      connection.off(eventName, stableHandler);
    };
    // handler intentionally excluded — accessed via handlerRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, eventName, once]);
}